/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import type { uiTypes, CoreAppStore } from '@opengeoweb/store';
import { uiSelectors } from '@opengeoweb/store';
import { Polygons } from '@opengeoweb/theme';
import { MapControlButton } from '@opengeoweb/webmap-react';
import { areaDrawingDialogType } from '../../store/warningsDrawings/utils';
import { useDrawingDrawDialogAction } from '../DrawingTool/useDrawingDialogAction';

export const AreaDrawingManagerMapButtonConnect: React.FC<{
  mapId: string;
  source?: uiTypes.Source;
}> = ({ mapId, source = 'app' }) => {
  const areaDrawingDialogMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, areaDrawingDialogType),
  );
  const isAreaDrawingDialogOpen = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, areaDrawingDialogType),
  );

  const { openAreaDrawingDialog, closeAreaDrawingDialog } =
    useDrawingDrawDialogAction({
      mapId,
      source,
    });

  const onClick = (): void => {
    const shouldOpen =
      areaDrawingDialogMapId !== mapId ? true : !isAreaDrawingDialogOpen;

    shouldOpen ? openAreaDrawingDialog() : closeAreaDrawingDialog();
  };

  const isOpen = areaDrawingDialogMapId === mapId && isAreaDrawingDialogOpen;

  return (
    <MapControlButton
      aria-label={`Area and Drawing Manager Button for ${mapId}`}
      isActive={isOpen}
      onClick={onClick}
      title="Area and Drawing Manager"
    >
      <Polygons />
    </MapControlButton>
  );
};
